import React, { useCallback, useState, useEffect, useMemo } from "react";
import { Hooks } from "@xt/xtbase";
import cx from "classnames";
import { message } from "antd";
import styles from "./index.module.scss";
import logoImg from "@/assets/images/logo.png";
import { get_socialMediaList, type SocialMediaConfigItem } from "@/api/xt-app/social-media";
import { waitRes } from "utils/axios";
import { getHosts } from "utils/method";

const { useTranslation } = Hooks;

const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? "0.0.0";

/** 清理所有 PWA 缓存并更新：删除 Cache API 缓存、注销当前 SW、刷新页面以拉取新版本 */
async function clearPwaCacheAndUpdate(): Promise<void> {
  if (typeof window === "undefined" || !window.caches) return;

  try {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  } catch (e) {
    console.warn("[PWA] 清理缓存失败:", e);
  }

  if ("serviceWorker" in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.unregister();
    } catch (e) {
      console.warn("[PWA] 注销 Service Worker 失败:", e);
    }
  }

  window.location.reload();
}

const CHEVRON_RIGHT = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ICON_UPDATE = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_8981_32696)">
  <path d="M9.97003 0.449951C4.68002 0.449951 0.400024 4.72995 0.400024 10.02C0.400024 15.31 4.68002 19.59 9.97003 19.59C15.26 19.59 19.54 15.31 19.54 10.02C19.54 4.72995 15.25 0.449951 9.97003 0.449951ZM11.69 6.05995L12.05 5.27995C12.39 4.73995 12.9 5.18995 12.9 5.18995L14.54 7.92995C14.84 8.48995 14.16 8.66995 14.16 8.66995L11.15 9.33995C10.08 9.49995 10.41 8.72995 10.41 8.72995L10.78 7.94995C6.29002 6.29995 4.82002 9.46995 4.82002 9.46995C6.14003 3.94995 11.69 6.05995 11.69 6.05995ZM8.12002 14.02L7.78003 14.83C7.45003 15.4 6.93003 14.94 6.93003 14.94L5.21003 12.16C4.89003 11.59 5.57002 11.39 5.57002 11.39L8.57002 10.63C9.64003 10.44 9.33003 11.24 9.33003 11.24L8.98003 12.05C13.53 13.64 14.92 10.35 14.92 10.35C13.74 16.05 8.12002 14.02 8.12002 14.02Z" fill="white"/>
  </g>
  <defs>
  <clipPath id="clip0_8981_32696">
  <rect width="20" height="20" fill="white"/>
  </clipPath>
  </defs>
  </svg>
);

/** 是否以 PWA（独立窗口/桌面安装）方式运行 */
function useIsPwa() {
  const [isPwa, setIsPwa] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    const iosStandalone = !!(navigator as { standalone?: boolean }).standalone;
    setIsPwa(!!(standalone || iosStandalone));
  }, []);
  return isPwa;
}

function resolveSocialIconUrl(icon?: string): string | undefined {
  if (!icon || !icon.trim()) return undefined;
  const t = icon.trim();
  if (/^https?:\/\//i.test(t)) return t;
  const { host, protocol } = getHosts();
  const path = t.startsWith("/") ? t : `/${t}`;
  return `${protocol}//${host}${path}`;
}

type SocialLinkRow = {
  key: string;
  href: string;
  label: string;
  iconUrl?: string;
};

/** CMS/API 常返回英文展示名，按关键词映射到 i18n */
function resolveSocialLabel(name: string, t: (key: string) => string): string {
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();
  if (lower.includes("twitter")) return t("account.followUsOnTwitter");
  if (lower.includes("telegram")) return t("account.joinUsOnTelegram");
  if (lower.includes("linkedin")) return t("account.followUsOnLinkedin");
  return trimmed;
}

const UserAbout: React.FC = () => {
  const t = useTranslation();
  const [updating, setUpdating] = useState(false);
  const [socialFromApi, setSocialFromApi] = useState<SocialMediaConfigItem[] | null>(null);
  const isPwa = useIsPwa();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, err } = await waitRes(get_socialMediaList, {});
      if (cancelled) return;
      if (err || !Array.isArray(data)) {
        setSocialFromApi([]);
        return;
      }
      setSocialFromApi(data as SocialMediaConfigItem[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClearCache = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (updating) return;
      if (typeof window === "undefined" || !("caches" in window)) {
        message.info(t("account.clearCacheUnsupported") || "Clear cache is not supported in this environment");
        return;
      }
      setUpdating(true);
      message.loading(t("account.clearCacheIng") || "Clearing cache…", 0);
      try {
        await clearPwaCacheAndUpdate();
      } catch (err) {
        message.destroy();
        message.error(t("account.clearCacheFailed") || "Clear cache failed, please try again later");
        setUpdating(false);
      }
    },
    [t, updating]
  );

  const socialLinkRows = useMemo((): SocialLinkRow[] => {
    if (!socialFromApi?.length) return [];
    return socialFromApi.map((item) => ({
      key: `social-${item.id}`,
      href: item.linkUrl,
      label: resolveSocialLabel(item.name, t),
      iconUrl: resolveSocialIconUrl(item.icon),
    }));
  }, [socialFromApi, t]);

  const socialListReady = socialFromApi !== null;
  const showSocialEmpty = socialListReady && socialLinkRows.length === 0;

  return (
    <div className={cx(styles["about-wrapper"])}>
      <div className={cx(styles["about-card"], styles["base-card"])}>
        <div className={styles["about-icon"]}>
          <div className={styles["about-icon-inner"]}>
            <img src={logoImg} alt="Spikex" className={styles["about-logo"]} />
          </div>
        </div>
        <div className={styles["about-version"]}>V {APP_VERSION}</div>
      </div>

      <div className={cx(styles["follow-card"], styles["base-card"])}>
        <div className={styles["follow-title"]}>{t("account.followUs")}</div>
        <div className={styles["follow-list"]}>
          {showSocialEmpty ? (
            <div className={styles["follow-empty"]}>{t("account.noData")}</div>
          ) : (
            socialLinkRows.map((item) => {
              const iconEl =
                item.iconUrl != null ? (
                  <img src={item.iconUrl} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className={styles["follow-icon-fallback"]}>{item.label.trim().charAt(0).toUpperCase()}</span>
                );
              const isHttp = item.href.startsWith("http");
              return (
                <a
                  key={item.key}
                  href={item.href}
                  target={isHttp ? "_blank" : undefined}
                  rel={isHttp ? "noopener noreferrer" : undefined}
                  className={styles["follow-item"]}
                >
                  <span className={styles["follow-icon"]}>{iconEl}</span>
                  <span className={styles["follow-label"]}>{item.label}</span>
                  <span className={styles["follow-chevron"]}>{CHEVRON_RIGHT}</span>
                </a>
              );
            })
          )}
          {isPwa && socialListReady && (
            <a
              href="#"
              className={cx(styles["follow-item"], updating && styles["follow-item-disabled"])}
              onClick={handleClearCache}
              aria-disabled={updating ? true : undefined}
            >
              <span className={styles["follow-icon"]}>{ICON_UPDATE}</span>
              <span className={styles["follow-label"]}>{t("account.clearCache")}</span>
              <span className={styles["follow-chevron"]}>{CHEVRON_RIGHT}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAbout;
